import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { RemoveFormula } from "building-types/options/formula";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { GenerateRoofOptions } from "building-types/roofs/roofGenerationTypes";
import type { RoofInputSettings, GetMergedSettingsForWindow } from "building-types/roofs/roofInputSettings";
import type {
  EvaluatedRoof,
  Roof,
  RoofDormerPlacement,
  RoofOpeningPlacement,
  RoofSystemInternal,
  RoofSystemTableInternal,
} from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolume, GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import {
  BuildingGenerationIssueType,
  type BuildingGenerationIssue,
} from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import type { WindowSettings } from "building-types/settings/windowSettings";
import type { WindowWallWrappingSettings } from "building-types/settings/windowWallWrappingSettings";
import type { WindowHomeSettings } from "building-types/windows/windowMergeTypes";
import type {
  WindowDescriptor,
  GeneratedWindowPlacement,
  WindowUnit,
  WindowShutters,
} from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import deepequal from "common/utils/deepequal";
import objectFilter from "common/utils/objectKeyFilter";
import objectMap from "common/utils/objectMap";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { getWindowCatalog } from "doc/elements/window/windowCatalog";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { polyLine2Compare, polyLine2EdgeCount } from "geom/polyLine2";
import { getSubRoofBlocks } from "../elements/multiGableRoofTools";
import { getFinalRoofPlanSettings, getFinalDormerSettings, multiGableRoofNew } from "../elements/roofs";
import { evaluateRoofSystems } from "../elements/roofs/evaluateRoofSystems";
import { generateMultiGableRoof } from "../elements/roofs/generate/generateRoof";
import getMergedRoofSettings from "../elements/roofs/getMergedRoofSettings";
import {
  filterAndGetRoofSystems,
  getRoofLevelId,
  getRoofSystemLevelId,
  getSortedRoofs,
} from "../elements/roofs/getRoofSystems";
import { fixRoofSideOverrides, fixRoofEdgeOverrides } from "../elements/roofs/roofEdges";
import { nonEmptyGableSubRoof } from "../elements/roofs/roofSubRoofFilters";
import { addRoofComponentInstances } from "../generationTools/components/componentRoofs";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import { memoAsync } from "../utils/memo";

export type RoofSystemsSelect = ReturnType<typeof select>;

export function select({
  settings,
  doc: {
    assets,
    dormers, //TODO: Remove this, once we have a more focused solution for roofSystem regeneration when dormers change
    roofSystems,
    roofs,
    roofOpenings,
    skylights,
    optionSets,
    instanceOptionSets,
    edgeModifiers,
    entitySettings,
    settings: buildingDocSettings,
    profiles,
  },
  blocks,
  blockPerimeters,
  levels,
  optionChoices,
  mirror,
  mirrorTranslationX,
  modifiers,
  modifiersByInstanceChain,
  windowPlacements,
  windowUnits,
  windowShutters,
  entityDisabledByGroup,
  settingsSets: activeSettingsSets,
  roofVolumes,
  roofSystemLevels,
  roofSystemClipContexts,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  isolatedEntity,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    {
      roofSettings: settings.roof,
      dormerSettings: settings.dormer,
      roofFramingSettings: settings.roofFraming,
      dormers, //TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers change
      roofSystems,
      roofs,
      roofOpenings,
    },
    definitions,
    optionChoices,
  );

  const roofSettings = evalFormulas(
    settings.roof,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as RemoveFormula<RoofSettings>;
  const dormerSettings = evalFormulas(
    settings.dormer,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as DormerSettings;
  const roofFramingSettings = evalFormulas(
    settings.roofFraming,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as RoofFramingSettings;

  //TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers change
  const dormersFiltered = evalFormulas(
    dormers,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as Table<RoofDormerPlacement>;

  return {
    blockPerimeters,
    blockLevelIds: objectFilter(
      objectMap(blocks, (x) => x.levelId),
      (x) => !!blockPerimeters[x],
    ),
    roofSystems,
    roofs,
    levels,
    roofSettings,
    dormerSettings,
    roofFramingSettings,
    wallSettings: settings.wall,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles,
      profileSettings: settings.profile,
    },

    // Window
    windowCatalogSettings: settings.windowCatalog,
    windowShutterSettings: settings.windowShutters,
    windowMullionSettings: settings.windowMullions,
    windowMaterialSettings: settings.windowMaterial,
    exteriorWindowCasingSettings: settings.exteriorWindowCasing,
    interiorWindowCasingSettings: settings.interiorWindowCasing,
    windowFramingSettings: settings.windowFraming,
    windowSettings: settings.window,
    windowAwningSettings: settings.awning,

    // Merge window & roof settings params
    activeSettingsSets,
    entitySettings,
    settings: buildingDocSettings,
    optionChoices: resolvedOptionChoices,
    rawOptionChoices: optionChoices,
    definitions,

    exteriorTrimSettings: settings.exteriorTrim,
    propCatalog: settings.props,
    assets,
    wainscotingSettings: settings.wainscoting,
    awningSettings: settings.awning,

    mirror,
    mirrorTranslationX,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    roofOpenings,
    skylights,
    windowsFromBuilding: windowPlacements,
    windowUnitsFromBuilding: windowUnits,
    windowShuttersFromBuilding: windowShutters,
    entityDisabledByGroup,
    roofVolumes,
    roofSystemLevels,
    roofSystemClipContexts,
    dormers: dormersFiltered, // TODO: Remove this, once we have a more focussed solution for roofSystem regeneration when dormers change
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    addRoofWallFaces: !enabledFeatures.wallPlanes,
  };
}

function compare(a: ReturnType<typeof select>, b: ReturnType<typeof select>) {
  if (!a || !b) {
    return false;
  }

  for (const k in a) {
    if (k === "blockPerimeters") {
      const aa = a[k];
      const bb = b[k];
      const oldPropNames = Object.getOwnPropertyNames(aa);
      const newPropNames = Object.getOwnPropertyNames(bb);

      if (oldPropNames.length !== newPropNames.length) {
        return false;
      }

      for (const blockId of oldPropNames) {
        if (!aa[blockId]) {
          return false;
        }
        if (!bb[blockId]) {
          return false;
        }

        if (aa[blockId].length !== bb[blockId].length) {
          return false;
        }

        for (let i = 0; i < aa[blockId].length; i++) {
          if (!polyLine2Compare(aa[blockId][i], bb[blockId][i])) {
            return false;
          }
        }
      }
    } else if (k === "roofVolumes") {
      return a[k] === b[k];
    } else if (!deepequal(a[k], b[k])) {
      return false;
    }
  }

  return true;
}

async function generate(
  opts: ReturnType<typeof select>,
): Promise<Pick<Building, "roofSystems" | "windowPlacements" | "windowUnits" | "windowShutters" | "issues">> {
  const generatedRoofSystems: RWTable<GeneratedRoofSystem> = {};
  const issues: BuildingGenerationIssue[] = [];
  const {
    blockPerimeters,
    blockLevelIds,
    levels,
    mirror,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    mirrorTranslationX,
    roofSystems: initialRoofSystems,
    roofs: initialRoofs,
    windowsFromBuilding,
    windowUnitsFromBuilding,
    windowShuttersFromBuilding,
    windowSettings,
    windowCatalogSettings,
    roofSystemLevels,
    roofSystemClipContexts,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    roofOpenings,
    skylights,
    entityDisabledByGroup,
  } = opts;

  const windowPlacements = { ...windowsFromBuilding };
  const windowUnits = { ...windowUnitsFromBuilding };
  const windowShutters = { ...windowShuttersFromBuilding };

  const windowCatalog = getWindowCatalog(windowCatalogSettings);

  let { roofSystems: evaluatedRoofSystems, roofs: evaluatedRoofs } = evaluateRoofSystems({
    initialRoofSystems,
    initialRoofs,
    optionChoices: opts.optionChoices,
    entityDisabledByGroup,
  });

  if (enabledFeatures.modelComponents) {
    const augmented = addRoofComponentInstances(
      evaluatedRoofSystems,
      evaluatedRoofs,
      initialRoofSystems,
      initialRoofs,
      idToParents,
      componentToTransforms,
      levels,
      isolatedEntity,
      roofOpenings,
      skylights,
      opts.definitions,
      opts.rawOptionChoices,
    );
    evaluatedRoofSystems = augmented.roofSystems;
    evaluatedRoofs = augmented.roofs;
    opts = {
      ...opts,
      roofOpenings: augmented.roofOpenings,
      skylights: augmented.skylights,
    };
  }

  const expandedRoofSystems = filterAndGetRoofSystems({
    unfilteredRoofSystems: evaluatedRoofSystems,
    unfilteredRoofs: evaluatedRoofs,
    levels,
    blockPerimeters,
    blockLevelIds,
    mirror,
    mirrorTranslationX,
    modifiers,
    edgeModifiers,
    modifiersByInstanceChain,
    idToParents,
    isolatedEntity,
  });

  for (const roofSystemId in expandedRoofSystems) {
    try {
      await generateRoofSystem({
        params: opts,
        roofSystems: expandedRoofSystems,
        roofSystemId,
        roofSystemLevels,
        roofSystemClipContexts,
        generatedRoofSystems,
        windowCatalog,
        windowPlacements: windowPlacements,
        windowSettings,
        windowUnits,
        windowShutters,
        issues,
        mirror,
      });
    } catch (e) {
      console.error(e);
      issues.push({
        type: BuildingGenerationIssueType.ROOF_SYSTEM_GENERATION_ERROR,
        message: e instanceof Error ? e.message : String(e),
        handle: { type: "ROOF_SYSTEM", id: roofSystemId },
      });
    }
  }

  return {
    roofSystems: generatedRoofSystems,
    windowPlacements: windowPlacements,
    windowUnits,
    windowShutters,
    issues,
  };
}

interface GenerateMultiGableRoofSystemOptions extends MergeSettingsForEntityParams {
  readonly level: Level;
  readonly roofSystemLevels: Table<RoofSystemLevel>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly roofSystemId: string;
  readonly roofSystem: RoofSystemInternal;
  readonly filteredSubRoofs: Roof[];
  readonly roofInputSettings: RoofInputSettings;
  readonly blockLevelIds: { [blockId: string]: string };
  readonly blockPerimeters: BlockPerimeters;
  readonly levels: Table<Level>;
  readonly windowCatalog: Table<WindowDescriptor>;
  readonly windows: Table<GeneratedWindowPlacement>;
  readonly windowUnits: Table<WindowUnit>;
  readonly windowShutters: Table<WindowShutters>;
  readonly issues: BuildingGenerationIssue[];
  readonly mirror: boolean;
  readonly roofOpenings: Table<RoofOpeningPlacement>;
  readonly roofVolumes: Table<GeneratedRoofVolume>;
  readonly addRoofWallFaces: boolean;
}

export async function generateRoofSystemInternal(
  opts: GenerateMultiGableRoofSystemOptions,
): Promise<GeneratedRoofSystem | null> {
  const {
    level,
    roofSystemLevels: multiGableRoofLevels,
    clipContext,
    roofSystemId,
    roofSystem,
    filteredSubRoofs,
    roofInputSettings,
    blockLevelIds,
    blockPerimeters,
    levels,
    issues,
    windowCatalog,
    windows,
    windowUnits,
    windowShutters,
    mirror,
    roofOpenings,
    entitySettings,
    activeSettingsSets,
    optionChoices,
    settings,
    roofVolumes,
    addRoofWallFaces,
  } = opts;

  const { roofSettings, roofPlanSettings, roofFramingSettings, dormerSettings } = roofInputSettings;
  const multiGableRoofLevel = multiGableRoofLevels[level.id];
  const { roofPerimeters: subGableRoofsPerimeters } = multiGableRoofLevel;
  const { subRoofs } = roofSystem;

  const insertedRoofs: RWTable<GeneratedRoof> = {};
  const insertedRoofVolumes: RWTable<GeneratedRoofVolumeLegacy> = {};

  if (!(Object.values(roofSystem.subRoofs).length > 0)) {
    console.assert(false, "Multi gable system must have one or more roofs");
  }

  const blockIdToSubRoofId: RWTable<string> = {};

  // find the valid subroofs
  const validSubRoofsIds: string[] = [];
  for (const { id } of filteredSubRoofs) {
    if (subGableRoofsPerimeters[id] !== undefined) {
      validSubRoofsIds.push(id);
    }
  }

  if (validSubRoofsIds.length === 0) {
    return null;
  }

  // iterate through valid subroofs
  for (const roofId of validSubRoofsIds) {
    const inputPerimeters = subGableRoofsPerimeters[roofId];
    const subRoof = subRoofs[roofId];
    const { boundingPerimeter } = inputPerimeters;

    if (!boundingPerimeter) {
      continue;
    }

    const roofVolume = roofVolumes[roofId];
    if (!roofVolume || roofVolume.entityType !== "ROOF_VOLUME_LEGACY") {
      continue;
    }

    const finalRoofSettings = getMergedRoofSettings({
      roof: subRoof,
      homeSettings: roofSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings,
    });

    const finalRoofPlanSettings = getFinalRoofPlanSettings(subRoof.roofPlanSettings, roofPlanSettings);
    const finalRoofFramingSettings = roofFramingSettings;
    const finalDormerSettings = getFinalDormerSettings(subRoof.dormerSettings, dormerSettings);

    const { peakDirection } = finalRoofSettings;
    const combinedMirror = isCombinedGlobalInstanceMirror(mirror, subRoof);

    const currLevelId = getRoofLevelId(subRoof, blockLevelIds, levels);
    const currLevel = levels[currLevelId];
    const currCeilingHeight = currLevel.floorElevation + currLevel.topOfWall;
    const subRoofBlocks = getSubRoofBlocks(subRoof, blockPerimeters);

    const filteredRoofOpenings: RoofOpeningPlacement[] = Object.values(roofOpenings).filter(
      (x) => x.parentId === subRoof.id && !x.disabled,
    );

    const finalRoofInputSettings: RoofInputSettings = {
      ...roofInputSettings,
      dormerSettings: finalDormerSettings,
      roofSettings: finalRoofSettings,
      roofPlanSettings: finalRoofPlanSettings,
      roofFramingSettings: finalRoofFramingSettings,
    };

    const roofData: EvaluatedRoof = {
      ...subRoof,
      roofSideOverrides: fixRoofSideOverrides(subRoof.roofSideOverrides, combinedMirror, peakDirection),
      roofEdgeOverrides: fixRoofEdgeOverrides(
        subRoof.roofEdgeOverrides,
        polyLine2EdgeCount(inputPerimeters.exactPerimeter),
        finalRoofSettings,
        subRoof.blocks,
        combinedMirror,
      ),
    };

    // generate sub roof
    const subGableOpts: GenerateRoofOptions = {
      roofVolume,
      roofData,
      roofId,
      name: subRoof.name,
      roofInputSettings: finalRoofInputSettings,
      roofOpenings: filteredRoofOpenings,
      inputPerimeters,
      ceilingHeight: currCeilingHeight,
      topOfWallHeight: currCeilingHeight,
      insertedRoofs,
      issues,
      windowCatalog,
      windows,
      windowUnits,
      windowShutters,
      mirror: combinedMirror,
      addRoofWallFaces,
    };

    const generatedRoof = await generateMultiGableRoof(subGableOpts);

    // insert sub roofs
    for (const subRoofBlock of subRoofBlocks) {
      blockIdToSubRoofId[subRoofBlock] = roofId;
    }

    insertedRoofVolumes[roofId] = roofVolume;
    insertedRoofs[roofId] = generatedRoof;
  }

  // create the whole gable roof system
  return multiGableRoofNew(
    roofSystemId,
    roofSystem.name,
    level.id,
    insertedRoofVolumes,
    insertedRoofs,
    blockIdToSubRoofId,
    clipContext,
  );
}

interface GenerateRoofSystemOptions {
  params: RoofSystemsSelect;
  roofSystems: RoofSystemTableInternal;
  roofSystemId: string;
  roofSystemLevels: Table<RoofSystemLevel>;
  roofSystemClipContexts: Table<MultiGableRoofClipContext>;
  generatedRoofSystems: RWTable<GeneratedRoofSystem>;
  windowCatalog: Table<WindowDescriptor>;
  windowPlacements: Table<GeneratedWindowPlacement>;
  windowSettings: WindowSettings;
  windowUnits: Table<WindowUnit>;
  windowShutters: Table<WindowShutters>;
  issues: BuildingGenerationIssue[];
  mirror: boolean;
}

export async function generateRoofSystem({
  params,
  roofSystems,
  roofSystemId,
  roofSystemLevels,
  roofSystemClipContexts,
  generatedRoofSystems,
  windowCatalog,
  windowPlacements: windows,
  windowUnits,
  windowShutters,
  issues,
  mirror,
}: GenerateRoofSystemOptions): Promise<void> {
  const {
    levels,
    blockLevelIds,
    blockPerimeters,
    optionChoices,
    wallSettings,
    roofSettings,
    dormerSettings,
    roofFramingSettings,
    profileSource,
    exteriorTrimSettings,
    propCatalog,
    assets,
    windowMaterialSettings,
    wainscotingSettings,
    windowShutterSettings,
    exteriorWindowCasingSettings,
    interiorWindowCasingSettings,
    awningSettings,
    windowMullionSettings,
    windowFramingSettings,
    windowSettings,
    entitySettings,
    settings: buildingDocSettings,
    activeSettingsSets,
    roofVolumes,
    addRoofWallFaces,
    entityDisabledByGroup,
  } = params;

  let { roofOpenings } = params;

  roofOpenings = evalFormulas(
    roofOpenings,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as Table<RoofOpeningPlacement>;

  const roofSystem = roofSystems[roofSystemId];

  const windowHomeSettings: WindowHomeSettings = {
    windowShutters: windowShutterSettings,
    exteriorWindowCasing: exteriorWindowCasingSettings,
    interiorWindowCasing: interiorWindowCasingSettings,
    awning: awningSettings,
    windowMullions: windowMullionSettings,
    windowFraming: windowFramingSettings,
    windowWrapping: buildingDocSettings.windowWrapping as WindowWallWrappingSettings,
    window: windowSettings,
    windowMaterial: windowMaterialSettings,
  };

  const getMergedSettingsForWindow: GetMergedSettingsForWindow = (window, descriptor) =>
    getMergedWindowSettings({
      window,
      descriptor,
      homeSettings: windowHomeSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings: buildingDocSettings,
    });

  const roofInputSettings: RoofInputSettings = {
    roofSettings,
    roofFramingSettings,
    dormerSettings,
    wallSettings,
    exteriorTrimSettings,
    propCatalog,
    assets,
    wainscotingSettings,
    profileSource,
    roofPlanSettings: {},
    roofElevationPlanSettings: {},
    getMergedSettingsForWindow,
  };

  const filteredSubRoofs = getSortedRoofs(roofSystem, nonEmptyGableSubRoof);

  if (filteredSubRoofs.length === 0) {
    return;
  }

  const roofSystemLevelId = getRoofSystemLevelId(roofSystem, blockLevelIds, levels);
  const level = roofSystemLevelId !== null ? levels[roofSystemLevelId] : undefined;

  if (!level) {
    console.error(
      `Cannot generate roof system as level id ${roofSystemLevelId} not found for roof system ${roofSystem.name}. It may have been deleted or disabled.`,
    );
    return;
  }

  assertIsNotNullOrUndefined(roofSystemLevelId);

  const clipContext = roofSystemClipContexts[roofSystemLevelId];

  const generatedRoofSystem = await generateRoofSystemInternal({
    level,
    roofSystemLevels,
    clipContext,
    roofSystemId,
    roofSystem,
    filteredSubRoofs,
    roofInputSettings,
    blockLevelIds,
    blockPerimeters,
    levels,
    windowCatalog,
    windows,
    windowUnits,
    windowShutters,
    issues,
    mirror,
    roofOpenings,
    entitySettings,
    activeSettingsSets,
    optionChoices,
    settings: buildingDocSettings,
    roofVolumes,
    addRoofWallFaces,
  });

  if (generatedRoofSystem) {
    generatedRoofSystems[roofSystemId] = generatedRoofSystem;
  }
}

export default memoAsync(select, generate, compare);
